/**
 * Figures de géométrie plane : une construction, élément par élément, comme au tableau (points, segments, droites,
 * cercles, angles, points construits : milieu, intersection, projeté). Une seule description sert à l'app (figure
 * manipulable : points déplaçables, curseurs), au studio (éditeur « une ligne par élément ») et à la vidéo (chaque
 * élément se trace quand la voix le nomme). Tout le calcul est ici, sans dépendance : testable et identique partout.
 */
import { z } from "zod";
import { compileExpr } from "./expr.js";
import { FigureSlider } from "./figure.js";

export const GEOMETRY_LIMITS = { elements: 40, sliders: 3, polygonPoints: 8 } as const;

const Range = z.tuple([z.number().finite(), z.number().finite()]).refine(([a, b]) => b > a && b - a <= 1000, "Intervalle invalide");
/** Identifiant d'un élément : « A », « B' », « d1 », « C2 »… (lettre puis lettres, chiffres, prime). */
export const GeoId = z.string().regex(/^[A-Za-z][A-Za-z0-9_']{0,11}$/, "Nom d'élément : une lettre puis lettres, chiffres ou ' (12 au plus)");
/** Nombre, ou expression des curseurs (« 1 - k », « 3*cos(t) »). */
const Num = z.union([z.number().finite(), z.string().trim().min(1).max(80)]);
const Color = z.enum(["teal", "amber", "ink", "muted"]);
const Common = { id: GeoId, color: Color.optional(), dashed: z.boolean().optional(), hidden: z.boolean().optional(), label: z.string().max(16).optional() };

export const GeoElement = z.discriminatedUnion("type", [
  z.object({ type: z.literal("point"), ...Common, x: Num, y: Num, draggable: z.boolean().optional() }),
  z.object({ type: z.literal("midpoint"), ...Common, of: z.tuple([GeoId, GeoId]) }),
  z.object({ type: z.literal("intersection"), ...Common, of: z.tuple([GeoId, GeoId]) }),
  z.object({ type: z.literal("projection"), ...Common, point: GeoId, onto: GeoId }),
  z.object({ type: z.literal("segment"), ...Common, from: GeoId, to: GeoId, marks: z.number().int().min(0).max(3).optional(), showLength: z.boolean().optional() }),
  z.object({ type: z.literal("line"), ...Common, through: z.tuple([GeoId, GeoId]) }),
  z.object({ type: z.literal("ray"), ...Common, from: GeoId, through: GeoId }),
  z.object({ type: z.literal("parallel"), ...Common, through: GeoId, to: GeoId }),
  z.object({ type: z.literal("perpendicular"), ...Common, through: GeoId, to: GeoId }),
  z.object({ type: z.literal("circle"), ...Common, center: GeoId, radius: Num.optional(), through: GeoId.optional() }),
  z.object({ type: z.literal("polygon"), ...Common, points: z.array(GeoId).min(3).max(GEOMETRY_LIMITS.polygonPoints), fill: z.boolean().optional() }),
  z.object({ type: z.literal("angle"), ...Common, from: GeoId, vertex: GeoId, to: GeoId, right: z.boolean().optional(), showValue: z.boolean().optional(), marks: z.number().int().min(0).max(3).optional() }),
  z.object({ type: z.literal("vector"), ...Common, from: GeoId, to: GeoId }),
  z.object({ type: z.literal("text"), ...Common, x: Num, y: Num, text: z.string().min(1).max(60) }),
]);
export type GeoElement = z.infer<typeof GeoElement>;
export type GeoType = GeoElement["type"];

const POINT_TYPES = new Set<GeoType>(["point", "midpoint", "intersection", "projection"]);
const LINE_TYPES = new Set<GeoType>(["segment", "line", "ray", "parallel", "perpendicular"]);
export const isPointType = (t: GeoType) => POINT_TYPES.has(t);
export const isLineType = (t: GeoType) => LINE_TYPES.has(t);

/** Références d'un élément vers les éléments qui le précèdent, avec la nature attendue. */
function refsOf(e: GeoElement): { id: string; want: "point" | "line" }[] {
  switch (e.type) {
    case "point": case "text": return [];
    case "midpoint": return e.of.map((id) => ({ id, want: "point" as const }));
    case "intersection": return e.of.map((id) => ({ id, want: "line" as const }));
    case "projection": return [{ id: e.point, want: "point" }, { id: e.onto, want: "line" }];
    case "segment": case "vector": return [{ id: e.from, want: "point" }, { id: e.to, want: "point" }];
    case "line": return e.through.map((id) => ({ id, want: "point" as const }));
    case "ray": return [{ id: e.from, want: "point" }, { id: e.through, want: "point" }];
    case "parallel": case "perpendicular": return [{ id: e.through, want: "point" }, { id: e.to, want: "line" }];
    case "circle": return [{ id: e.center, want: "point" }, ...(e.through ? [{ id: e.through, want: "point" as const }] : [])];
    case "polygon": return e.points.map((id) => ({ id, want: "point" as const }));
    case "angle": return [{ id: e.from, want: "point" }, { id: e.vertex, want: "point" }, { id: e.to, want: "point" }];
  }
}
/** Éléments dont dépend un élément (dans l'ordre de la construction). */
export const geoRefs = (e: GeoElement): string[] => refsOf(e).map((r) => r.id);

/** Mots qui désignent un type d'élément dans une narration (« la parallèle », « le cercle »…). */
const TYPE_WORDS: Partial<Record<GeoType, RegExp>> = {
  midpoint: /milieu/i, intersection: /coupe|intersection|rencontre/i, projection: /projet|pied|hauteur/i,
  parallel: /parall[eè]le/i, perpendicular: /perpendiculaire/i, circle: /cercle|compas|rayon/i, angle: /angle/i,
  polygon: /triangle|quadrilat|polygone|carr[ée]|rectangle|parall[ée]logramme|losange|trap[èe]ze/i, vector: /vecteur/i,
  ray: /demi-droite/i, line: /droite/i, segment: /segment|c[ôo]t[ée]/i, text: /./,
};
const escapeRe = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** La phrase nomme-t-elle cet élément ? « M », « A B C » (lettres dites une à une), « BC » ou « B C ». */
function mentions(line: string, id: string): boolean {
  if (!/^[A-Z]/.test(id)) return false; // « d », « t », « k » se confondent avec les mots
  const spoken = id.replace(/'/g, " prime").split("").join(" ").replace(/ {2,}/g, " ");
  for (const form of new Set([id, spoken])) {
    if (new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRe(form)}(?=$|[^\\p{L}\\p{N}'])`, "u").test(line)) return true;
  }
  return false;
}

/**
 * Temps de narration où chaque élément visible se trace : la première phrase qui le nomme (ou qui nomme son type),
 * jamais avant les éléments dont il dépend ; à défaut, dès que ses dépendances sont tracées.
 */
export function stageConstruction(spec: GeometrySpec, lines: string[]): Map<string, number> {
  const at = new Map<string, number>();
  const last = Math.max(0, lines.length - 1);
  for (const e of spec.elements) {
    const after = Math.max(0, ...geoRefs(e).map((r) => at.get(r) ?? 0));
    let beat = -1;
    for (let i = after; i < lines.length && beat < 0; i++) if (mentions(lines[i]!, e.id)) beat = i;
    if (beat < 0 && !/^[A-Z]/.test(e.id)) {
      const w = TYPE_WORDS[e.type];
      for (let i = after; i < lines.length && beat < 0; i++) if (w?.test(lines[i]!)) beat = i;
    }
    at.set(e.id, Math.min(last, beat < 0 ? after : beat));
  }
  return at;
}

const exprsOf = (e: GeoElement): (string | number)[] => (e.type === "point" || e.type === "text" ? [e.x, e.y] : e.type === "circle" && e.radius !== undefined ? [e.radius] : []);

export const GeometrySpec = z.object({
  kind: z.literal("geometry"),
  view: z.object({ x: Range, y: Range }),
  grid: z.boolean().default(true),
  axes: z.boolean().default(false),
  unit: z.string().max(6).default("cm"),
  sliders: z.array(FigureSlider).max(GEOMETRY_LIMITS.sliders).default([]),
  elements: z.array(GeoElement).min(1).max(GEOMETRY_LIMITS.elements),
}).superRefine((s, ctx) => {
  const names = s.sliders.map((x) => x.name);
  const seen = new Map<string, GeoType>();
  s.elements.forEach((e, i) => {
    if (seen.has(e.id)) ctx.addIssue({ code: "custom", path: ["elements", i, "id"], message: `${e.id} : nom déjà utilisé` });
    if (names.includes(e.id)) ctx.addIssue({ code: "custom", path: ["elements", i, "id"], message: `${e.id} : c'est déjà le nom d'un curseur` });
    for (const r of refsOf(e)) {
      const t = seen.get(r.id);
      if (!t) ctx.addIssue({ code: "custom", path: ["elements", i], message: `${e.id} : « ${r.id} » n'est pas défini avant` });
      else if (r.want === "point" && !isPointType(t)) ctx.addIssue({ code: "custom", path: ["elements", i], message: `${e.id} : « ${r.id} » doit être un point` });
      else if (r.want === "line" && !isLineType(t)) ctx.addIssue({ code: "custom", path: ["elements", i], message: `${e.id} : « ${r.id} » doit être une droite, un segment ou une demi-droite` });
    }
    if (e.type === "circle" && (e.radius === undefined) === (e.through === undefined)) ctx.addIssue({ code: "custom", path: ["elements", i], message: `${e.id} : donner un rayon ou un point du cercle` });
    for (const x of exprsOf(e)) {
      if (typeof x === "number") continue;
      const r = compileExpr(x, names);
      if (!r.ok) ctx.addIssue({ code: "custom", path: ["elements", i], message: `${e.id} : ${r.error}` });
    }
    seen.set(e.id, e.type);
  });
});
export type GeometrySpec = z.infer<typeof GeometrySpec>;

// --- Calcul ---------------------------------------------------------------------------------------------------

export type Vec2 = [number, number];
export type GeoResolved =
  | { kind: "point"; p: Vec2 }
  | { kind: "line"; a: Vec2; b: Vec2; extent: "segment" | "line" | "ray" }
  | { kind: "circle"; c: Vec2; r: number }
  | { kind: "polygon"; pts: Vec2[] }
  | { kind: "angle"; v: Vec2; start: number; sweep: number; deg: number }
  | { kind: "vector"; a: Vec2; b: Vec2 }
  | { kind: "text"; p: Vec2; text: string };

const EPS = 1e-9;
const sub = (a: Vec2, b: Vec2): Vec2 => [a[0] - b[0], a[1] - b[1]];
const add = (a: Vec2, b: Vec2): Vec2 => [a[0] + b[0], a[1] + b[1]];
const mul = (a: Vec2, k: number): Vec2 => [a[0] * k, a[1] * k];
const dot = (a: Vec2, b: Vec2) => a[0] * b[0] + a[1] * b[1];
const cross = (a: Vec2, b: Vec2) => a[0] * b[1] - a[1] * b[0];
export const dist = (a: Vec2, b: Vec2) => Math.hypot(a[0] - b[0], a[1] - b[1]);

const evalNum = (x: string | number, params: Record<string, number>, names: string[]) => {
  if (typeof x === "number") return x;
  const r = compileExpr(x, names);
  const v = r.ok ? r.expr.fn(params) : NaN;
  return Number.isFinite(v) ? v : NaN;
};

/**
 * Position de chaque élément pour des valeurs de curseurs données ; `moved` remplace la position des points déplacés
 * par l'élève. Un élément impossible à construire (droites parallèles sans intersection…) est absent du résultat,
 * ainsi que tout ce qui en dépend.
 */
export function resolveGeometry(spec: GeometrySpec, params: Record<string, number> = {}, moved: Record<string, Vec2> = {}): Map<string, GeoResolved> {
  const names = spec.sliders.map((s) => s.name);
  const vals = { ...Object.fromEntries(spec.sliders.map((s) => [s.name, s.initial])), ...params };
  const out = new Map<string, GeoResolved>();
  const pt = (id: string) => { const r = out.get(id); return r?.kind === "point" ? r.p : null; };
  const ln = (id: string) => { const r = out.get(id); return r?.kind === "line" ? r : null; };
  for (const e of spec.elements) {
    let r: GeoResolved | null = null;
    switch (e.type) {
      case "point": {
        const p: Vec2 = moved[e.id] ?? [evalNum(e.x, vals, names), evalNum(e.y, vals, names)];
        if (Number.isFinite(p[0]) && Number.isFinite(p[1])) r = { kind: "point", p };
        break;
      }
      case "midpoint": { const a = pt(e.of[0]), b = pt(e.of[1]); if (a && b) r = { kind: "point", p: mul(add(a, b), 0.5) }; break; }
      case "intersection": {
        const l1 = ln(e.of[0]), l2 = ln(e.of[1]);
        if (!l1 || !l2) break;
        const d1 = sub(l1.b, l1.a), d2 = sub(l2.b, l2.a), den = cross(d1, d2);
        if (Math.abs(den) < EPS) break; // parallèles
        const t = cross(sub(l2.a, l1.a), d2) / den;
        r = { kind: "point", p: add(l1.a, mul(d1, t)) };
        break;
      }
      case "projection": {
        const p = pt(e.point), l = ln(e.onto);
        if (!p || !l) break;
        const d = sub(l.b, l.a), n = dot(d, d);
        if (n < EPS) break;
        r = { kind: "point", p: add(l.a, mul(d, dot(sub(p, l.a), d) / n)) };
        break;
      }
      case "segment": { const a = pt(e.from), b = pt(e.to); if (a && b) r = { kind: "line", a, b, extent: "segment" }; break; }
      case "line": { const a = pt(e.through[0]), b = pt(e.through[1]); if (a && b && dist(a, b) > EPS) r = { kind: "line", a, b, extent: "line" }; break; }
      case "ray": { const a = pt(e.from), b = pt(e.through); if (a && b && dist(a, b) > EPS) r = { kind: "line", a, b, extent: "ray" }; break; }
      case "parallel": case "perpendicular": {
        const p = pt(e.through), l = ln(e.to);
        if (!p || !l) break;
        const d = sub(l.b, l.a);
        const dir: Vec2 = e.type === "parallel" ? d : [-d[1], d[0]];
        if (Math.hypot(dir[0], dir[1]) < EPS) break;
        r = { kind: "line", a: p, b: add(p, dir), extent: "line" };
        break;
      }
      case "circle": {
        const c = pt(e.center);
        if (!c) break;
        const rad = e.through ? (pt(e.through) ? dist(c, pt(e.through)!) : NaN) : evalNum(e.radius!, vals, names);
        if (Number.isFinite(rad) && rad > 0) r = { kind: "circle", c, r: rad };
        break;
      }
      case "polygon": { const pts = e.points.map(pt); if (pts.every(Boolean)) r = { kind: "polygon", pts: pts as Vec2[] }; break; }
      case "angle": {
        const a = pt(e.from), v = pt(e.vertex), b = pt(e.to);
        if (!a || !v || !b || dist(a, v) < EPS || dist(b, v) < EPS) break;
        const start = Math.atan2(a[1] - v[1], a[0] - v[0]);
        let sweep = Math.atan2(b[1] - v[1], b[0] - v[0]) - start;
        while (sweep > Math.PI) sweep -= 2 * Math.PI;
        while (sweep <= -Math.PI) sweep += 2 * Math.PI;
        r = { kind: "angle", v, start, sweep, deg: Math.abs(sweep) * 180 / Math.PI };
        break;
      }
      case "vector": { const a = pt(e.from), b = pt(e.to); if (a && b) r = { kind: "vector", a, b }; break; }
      case "text": { const p: Vec2 = [evalNum(e.x, vals, names), evalNum(e.y, vals, names)]; if (Number.isFinite(p[0]) && Number.isFinite(p[1])) r = { kind: "text", p, text: e.text }; break; }
    }
    if (r) out.set(e.id, r);
  }
  return out;
}

/** Extrémités visibles d'une droite ou d'une demi-droite dans le cadre de la figure (null si elle n'y passe pas). */
export function clipLine(a: Vec2, b: Vec2, extent: "segment" | "line" | "ray", view: { x: [number, number]; y: [number, number] }): [Vec2, Vec2] | null {
  if (extent === "segment") return [a, b];
  const d = sub(b, a);
  let t0 = extent === "ray" ? 0 : -Infinity, t1 = Infinity;
  const clip = (p: number, q: number) => { // p·t ≤ q
    if (Math.abs(p) < EPS) return q >= 0;
    const t = q / p;
    if (p < 0) { if (t > t1) return false; if (t > t0) t0 = t; } else { if (t < t0) return false; if (t < t1) t1 = t; }
    return true;
  };
  const [x0, x1] = view.x, [y0, y1] = view.y;
  if (!clip(-d[0], a[0] - x0) || !clip(d[0], x1 - a[0]) || !clip(-d[1], a[1] - y0) || !clip(d[1], y1 - a[1])) return null;
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 < t0) return null;
  return [add(a, mul(d, t0)), add(a, mul(d, t1))];
}

/** Nom affiché d'un élément : son étiquette, sinon le nom d'un point (A, B', M1), sinon rien. */
export function geoLabel(e: GeoElement): string | null {
  if (e.label !== undefined) return e.label || null;
  if (isPointType(e.type) && /^[A-Z][0-9']*$/.test(e.id)) return e.id;
  return null;
}
export const initialGeometryParams = (s: GeometrySpec) => Object.fromEntries(s.sliders.map((x) => [x.name, x.initial]));
/** Spec lue telle quelle (studio) ou null si elle n'est pas valide. */
export function readGeometrySpec(raw: unknown): GeometrySpec | null {
  const r = GeometrySpec.safeParse(raw);
  return r.success ? r.data : null;
}

// --- Écriture « une ligne par élément » (studio, IA) ----------------------------------------------------------

/**
 * Syntaxe lue et écrite par l'éditeur du studio :
 *   view(-1, 7, -1, 6)          cadre (x min, x max, y min, y max) ; « grid » ou « nogrid », « axes »
 *   k = slider(0.2, 0.9, 0.05, 0.5) "Position de M"   curseur (min, max, pas, valeur initiale) et libellé
 *   A = point(1, 5) draggable   point (coordonnées : nombres ou expressions des curseurs)
 *   M = midpoint(A, B) · I = intersection(d1, d2) · H = projection(A, d)
 *   s = segment(A, B) marks=2 length · d = line(A, B) · r = ray(A, B) · p = parallel(M, s) · q = perpendicular(A, d)
 *   c = circle(O, 3) ou circle(O, A) · t = polygon(A, B, C) fill · a = angle(B, A, C) right value · v = vector(A, B)
 *   n = text(2, 4, "(d)")
 * Options : draggable, hidden, dashed, teal|amber|ink|muted, fill, right, value, length, marks=1..3, label="…", nolabel.
 */
const TYPES_ARGS: Record<string, number | [number, number]> = { point: 2, midpoint: 2, intersection: 2, projection: 2, segment: 2, line: 2, ray: 2, parallel: 2, perpendicular: 2, circle: 2, polygon: [3, GEOMETRY_LIMITS.polygonPoints], angle: 3, vector: 2, text: 3, slider: 4 };

function splitArgs(s: string): string[] {
  const out: string[] = [];
  let depth = 0, cur = "", quote = false;
  for (const ch of s) {
    if (ch === '"') quote = !quote;
    if (!quote && ch === "(") depth++;
    if (!quote && ch === ")") depth--;
    if (!quote && depth === 0 && ch === ",") { out.push(cur.trim()); cur = ""; continue; }
    cur += ch;
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}
const num = (s: string): string | number => (/^-?\d+(\.\d+)?$/.test(s.replace(",", ".")) && !s.includes(",") ? Number(s) : s);

export type ConstructionResult = { spec: GeometrySpec | null; errors: { line: number; message: string }[] };

/** Texte → spec (avec la validation complète) ; erreurs avec leur numéro de ligne. */
export function parseConstruction(text: string): ConstructionResult {
  const errors: { line: number; message: string }[] = [];
  const raw: { kind: "geometry"; view: { x: [number, number]; y: [number, number] }; grid: boolean; axes: boolean; unit: string; sliders: unknown[]; elements: Record<string, unknown>[] } =
    { kind: "geometry", view: { x: [-1, 8], y: [-1, 7] }, grid: true, axes: false, unit: "cm", sliders: [], elements: [] };
  const lineOf = new Map<string, number>();
  text.split("\n").forEach((rawLine, i) => {
    const line = rawLine.replace(/#.*$/, "").trim();
    const n = i + 1;
    if (!line) return;
    if (line === "grid" || line === "nogrid" || line === "axes" || line === "noaxes") { if (line.endsWith("grid")) raw.grid = line === "grid"; else raw.axes = line === "axes"; return; }
    const unit = /^unit\s+(\S{1,6})$/.exec(line);
    if (unit) { raw.unit = unit[1]!; return; }
    const view = /^view\s*\(([^)]*)\)$/.exec(line);
    if (view) {
      const v = splitArgs(view[1]!).map(Number);
      if (v.length !== 4 || v.some((x) => !Number.isFinite(x))) { errors.push({ line: n, message: "view(x min, x max, y min, y max)" }); return; }
      raw.view = { x: [v[0]!, v[1]!], y: [v[2]!, v[3]!] };
      return;
    }
    const head = /^([A-Za-z][A-Za-z0-9_']*)\s*=\s*([a-z]+)\s*\(/.exec(line);
    // Arguments jusqu'à la parenthèse qui ferme la première (une étiquette « (T) » peut suivre).
    let close = -1;
    if (head) for (let i = head[0].length, depth = 1, quote = false; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') quote = !quote;
      else if (!quote && ch === "(") depth++;
      else if (!quote && ch === ")" && --depth === 0) { close = i; break; }
    }
    if (!head || close < 0) { errors.push({ line: n, message: "écrire « nom = type(arguments) options », par exemple « A = point(0, 0) »" }); return; }
    const id = head[1]!, type = head[2]!, argText = line.slice(head[0].length, close), optText = line.slice(close + 1).trim();
    const args = splitArgs(argText);
    const want = TYPES_ARGS[type];
    if (want === undefined) { errors.push({ line: n, message: `type inconnu « ${type} » (point, midpoint, intersection, projection, segment, line, ray, parallel, perpendicular, circle, polygon, angle, vector, text, slider)` }); return; }
    const [lo, hi] = Array.isArray(want) ? want : [want, want];
    if (args.length < lo || args.length > hi) { errors.push({ line: n, message: `${type} attend ${lo === hi ? lo : `${lo} à ${hi}`} argument${hi > 1 ? "s" : ""}` }); return; }
    // Options : mots clés, marks=2, label="…", et une chaîne entre guillemets (libellé d'un curseur).
    const opts: Record<string, string | true> = {};
    let quoted: string | undefined;
    for (const o of optText.match(/label="[^"]*"|"[^"]*"|\S+/g) ?? []) {
      if (o.startsWith('"')) quoted = o.slice(1, -1);
      else if (o.startsWith("label=")) opts.label = o.slice(7, -1);
      else if (o.includes("=")) { const [k, v] = o.split("="); opts[k!] = v ?? ""; }
      else opts[o] = true;
    }
    if (type === "slider") {
      const v = args.map(Number);
      if (v.some((x) => !Number.isFinite(x))) { errors.push({ line: n, message: "slider(min, max, pas, valeur initiale) : quatre nombres" }); return; }
      raw.sliders.push({ name: id, label: quoted ? { fr: quoted } : {}, min: v[0], max: v[1], step: v[2], initial: v[3] });
      return;
    }
    const e: Record<string, unknown> = { type, id };
    switch (type) {
      case "point": e.x = num(args[0]!); e.y = num(args[1]!); break;
      case "midpoint": case "intersection": e.of = args; break;
      case "projection": e.point = args[0]; e.onto = args[1]; break;
      case "segment": case "vector": e.from = args[0]; e.to = args[1]; break;
      case "line": e.through = args; break;
      case "ray": e.from = args[0]; e.through = args[1]; break;
      case "parallel": case "perpendicular": e.through = args[0]; e.to = args[1]; break;
      case "circle": {
        e.center = args[0];
        const second = args[1]!;
        if (lineOf.has(second)) e.through = second; else e.radius = num(second);
        break;
      }
      case "polygon": e.points = args; break;
      case "angle": e.from = args[0]; e.vertex = args[1]; e.to = args[2]; break;
      case "text": { e.x = num(args[0]!); e.y = num(args[1]!); const t = args[2]!; e.text = t.startsWith('"') && t.endsWith('"') ? t.slice(1, -1) : t; break; }
    }
    for (const c of ["teal", "amber", "ink", "muted"]) if (opts[c]) e.color = c;
    if (opts.draggable) e.draggable = true;
    if (opts.hidden) e.hidden = true;
    if (opts.dashed) e.dashed = true;
    if (opts.fill) e.fill = true;
    if (opts.right) e.right = true;
    if (opts.value) e.showValue = true;
    if (opts.length) e.showLength = true;
    if (typeof opts.marks === "string") e.marks = Number(opts.marks);
    if (typeof opts.label === "string") e.label = opts.label;
    if (opts.nolabel) e.label = "";
    lineOf.set(id, n);
    raw.elements.push(e);
  });
  if (errors.length) return { spec: null, errors };
  const r = GeometrySpec.safeParse(raw);
  if (r.success) return { spec: r.data, errors: [] };
  for (const issue of r.error.issues) {
    const idx = issue.path[0] === "elements" && typeof issue.path[1] === "number" ? issue.path[1] : null;
    const id = idx !== null ? (raw.elements[idx]?.id as string | undefined) : undefined;
    errors.push({ line: (id ? lineOf.get(id) : undefined) ?? 0, message: issue.message });
  }
  return { spec: null, errors };
}

const fmtNum = (x: string | number) => (typeof x === "number" ? String(Math.round(x * 1000) / 1000) : x);

/** Spec → texte de l'éditeur (inverse de parseConstruction). */
export function printConstruction(s: GeometrySpec): string {
  const lines = [`view(${s.view.x[0]}, ${s.view.x[1]}, ${s.view.y[0]}, ${s.view.y[1]})`];
  if (!s.grid) lines.push("nogrid");
  if (s.axes) lines.push("axes");
  if (s.unit !== "cm") lines.push(`unit ${s.unit}`);
  for (const sl of s.sliders) lines.push(`${sl.name} = slider(${sl.min}, ${sl.max}, ${sl.step}, ${sl.initial})${sl.label.fr ? ` "${sl.label.fr}"` : ""}`);
  for (const e of s.elements) {
    let call: string;
    switch (e.type) {
      case "point": call = `point(${fmtNum(e.x)}, ${fmtNum(e.y)})`; break;
      case "midpoint": case "intersection": call = `${e.type}(${e.of.join(", ")})`; break;
      case "projection": call = `projection(${e.point}, ${e.onto})`; break;
      case "segment": case "vector": call = `${e.type}(${e.from}, ${e.to})`; break;
      case "line": call = `line(${e.through.join(", ")})`; break;
      case "ray": call = `ray(${e.from}, ${e.through})`; break;
      case "parallel": case "perpendicular": call = `${e.type}(${e.through}, ${e.to})`; break;
      case "circle": call = `circle(${e.center}, ${e.through ?? fmtNum(e.radius!)})`; break;
      case "polygon": call = `polygon(${e.points.join(", ")})`; break;
      case "angle": call = `angle(${e.from}, ${e.vertex}, ${e.to})`; break;
      case "text": call = `text(${fmtNum(e.x)}, ${fmtNum(e.y)}, "${e.text}")`; break;
    }
    const o: string[] = [];
    if (e.color) o.push(e.color);
    if ("draggable" in e && e.draggable) o.push("draggable");
    if (e.hidden) o.push("hidden");
    if (e.dashed) o.push("dashed");
    if ("fill" in e && e.fill) o.push("fill");
    if ("right" in e && e.right) o.push("right");
    if ("showValue" in e && e.showValue) o.push("value");
    if ("showLength" in e && e.showLength) o.push("length");
    if ("marks" in e && e.marks) o.push(`marks=${e.marks}`);
    if (e.label === "") o.push("nolabel");
    else if (e.label !== undefined) o.push(`label="${e.label}"`);
    lines.push(`${e.id} = ${call}${o.length ? ` ${o.join(" ")}` : ""}`);
  }
  return lines.join("\n");
}

// --- Gabarits ----------------------------------------------------------------------------------------------------

const preset = (id: string, label: string, text: string) => ({ id, label, text, spec: parseConstruction(text).spec! });
export const GEOMETRY_PRESETS = [
  preset("triangle", "Triangle quelconque", `view(-1, 7, -1, 6)
A = point(1, 5) draggable
B = point(0, 0) draggable
C = point(6, 0) draggable
t = polygon(A, B, C)`),
  preset("triangle-rectangle", "Triangle rectangle (Pythagore)", `view(-1, 6, -1, 5)
A = point(0, 0)
B = point(4, 0)
C = point(0, 3)
AB = segment(A, B) length
AC = segment(A, C) length
BC = segment(B, C) amber length
a = angle(B, A, C) right`),
  preset("thales", "Thalès : (MN) parallèle à (BC)", `view(-1, 7, -1, 6)
k = slider(0.15, 0.9, 0.05, 0.5) "Position de M sur [AB]"
A = point(1, 5)
B = point(0, 0)
C = point(6, 0)
t = polygon(A, B, C)
BC = segment(B, C)
AC = segment(A, C) hidden
M = point(1 - k, 5 - 5*k)
d = parallel(M, BC) dashed muted
N = intersection(d, AC)
MN = segment(M, N) amber`),
  preset("cercle-tangente", "Cercle et tangente", `view(-5, 6, -4, 5)
O = point(0, 0)
A = point(3, 1.5) draggable
c = circle(O, A)
OA = segment(O, A)
T = perpendicular(A, OA) amber label="(T)"`),
  preset("parallelogramme", "Parallélogramme", `view(-1, 8, -1, 6)
A = point(0, 0) draggable
B = point(4, 0) draggable
D = point(1.5, 3) draggable
AB = segment(A, B)
AD = segment(A, D)
d1 = parallel(D, AB) hidden
d2 = parallel(B, AD) hidden
C = intersection(d1, d2)
p = polygon(A, B, C, D) fill`),
  preset("angle-inscrit", "Angle inscrit et angle au centre", `view(-5, 5, -5, 5)
O = point(0, 0)
A = point(-2.4, -1.8)
B = point(2.4, -1.8)
t = slider(0.4, 2.7, 0.05, 1.57) "Position de M sur le cercle"
M = point(3*cos(t), 3*sin(t))
c = circle(O, 3)
MA = segment(M, A)
MB = segment(M, B)
OA = segment(O, A) muted
OB = segment(O, B) muted
i = angle(A, M, B) value
o = angle(A, O, B) value amber`),
];
